import type { ReportContentBlock, ReportSection } from "./domain";
import type { ClientSectionContentState } from "./report-content-state";
import {
  sectionEightSimplifiedFieldDefinitions,
  sectionNineSimplifiedFieldDefinitions,
  sectionSevenSimplifiedFieldDefinitions,
  sectionSixSimplifiedFieldDefinitions,
} from "./simplified-assessment";

const blocksFromFields = <TFields extends object>(
  prefix:string,
  fields:TFields,
  definitions:Array<{key:keyof TFields;label:string}>,
):ReportContentBlock[] => definitions.flatMap((definition,index)=>{
  const content=String(fields[definition.key]??"").trim();
  return content?[{
    id:`${prefix}${String(definition.key)}`,
    type:index===0?"summary" as const:"paragraph" as const,
    title:definition.label,
    content,
    clientVisible:true,
    includeInPdf:true,
    evidenceIds:[],
  }]:[];
});

function sectionFourDraftBlocks(section:ReportSection):ReportContentBlock[] {
  const draft=section.sectionFourWorkflow?.clientDraft;
  if(!draft)return[];
  return [
    ["Introduction",draft.introduction],
    ["How the prompt testing works",draft.promptTestingExplanation??""],
    ["How the prompts were tested",draft.testingSummary],
    ["Strongest results",draft.strongestResults],
    ["Visibility gaps",draft.visibilityGaps],
    ["Platform comparison",draft.platformComparison],
    ["What this means commercially",draft.commercialMeaning],
    ["Evidence limitations",draft.limitations],
  ].flatMap(([title,content],index)=>content.trim()?[{
    id:`section4-client-preview-${index}`,
    type:index===0?"summary" as const:"paragraph" as const,
    title,
    content,
    clientVisible:true,
    includeInPdf:true,
    evidenceIds:[],
  }]:[]);
}

function sectionTenDraftBlocks(section:ReportSection):ReportContentBlock[] {
  const draft=section.sectionTenWorkflow?.v2ClientDraft;
  if(!draft)return[];
  const areaContent=(summary:string,points:string[])=>[summary,...points.map(point=>`â€¢ ${point}`)].filter(Boolean).join("\n");
  return [
    {id:"section10-client-v2-overview",type:"summary" as const,title:"Recommended work programme",content:draft.overview},
    ...draft.workAreas.map(area=>({id:`section10-client-v2-${area.id}`,type:"paragraph" as const,title:area.title,content:areaContent(area.summary,area.points)})),
    {id:"section10-client-v2-delivery-options",type:"paragraph" as const,title:"Delivery options",content:draft.deliveryOptions},
    {id:"section10-client-v2-next-decision",type:"paragraph" as const,title:"Next decision",content:draft.nextDecision},
    {id:"section10-client-v2-limitations",type:"paragraph" as const,title:"Important limitations",content:draft.evidenceLimitations},
  ].filter(block=>block.content.trim()).map(block=>({...block,clientVisible:true,includeInPdf:true,evidenceIds:[]}));
}

export function projectSectionForClientPreview(
  section:ReportSection,
  contentState:ClientSectionContentState,
):ReportSection {
  if(contentState.source!=="workflow draft"&&contentState.source!=="v2 draft")return section;
  if(section.id==="prompt-testing"){
    const blocks=sectionFourDraftBlocks(section);
    return blocks.length?{...section,blocks}:section;
  }
  if(section.id==="commercial-gaps"&&section.sectionSixWorkflow?.simplifiedClientDraft){
    return{...section,blocks:blocksFromFields("section6-client-v2-",section.sectionSixWorkflow.simplifiedClientDraft.fields,sectionSixSimplifiedFieldDefinitions)};
  }
  if(section.id==="competitors"&&section.sectionSevenWorkflow?.simplifiedClientDraft){
    return{...section,blocks:blocksFromFields("section7-client-v2-",section.sectionSevenWorkflow.simplifiedClientDraft.fields,sectionSevenSimplifiedFieldDefinitions)};
  }
  if(section.id==="authority-gaps"&&section.sectionEightWorkflow?.simplifiedClientDraft){
    return{...section,blocks:blocksFromFields("section8-client-v2-",section.sectionEightWorkflow.simplifiedClientDraft.fields,sectionEightSimplifiedFieldDefinitions)};
  }
  if(section.id==="positioning"&&section.sectionNineWorkflow?.simplifiedClientDraft){
    return{...section,blocks:blocksFromFields("section9-client-v2-",section.sectionNineWorkflow.simplifiedClientDraft.fields,sectionNineSimplifiedFieldDefinitions)};
  }
  if(section.id==="work-programme"&&section.sectionTenWorkflow?.v2ClientDraft){
    return{
      ...section,
      blocks:sectionTenDraftBlocks(section),
      sectionTenWorkflow:{...section.sectionTenWorkflow,v2ClientDraftApproved:true},
    };
  }
  return section;
}
